/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { AVM1Point, toAS3Point } from './AVM1Point';
import { alCoerceNumber, alDefineObjectProperties } from '../runtime';
import { Matrix } from '@awayjs/core';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1Function } from '../runtime/AVM1Function';
export function toAS3Matrix(v) {
    var context = v.context;
    var a, b, c, d, tx, ty;
    if (v instanceof AVM1Object) {
        a = alCoerceNumber(context, v.alGet('a'));
        b = alCoerceNumber(context, v.alGet('b'));
        c = alCoerceNumber(context, v.alGet('c'));
        d = alCoerceNumber(context, v.alGet('d'));
        tx = alCoerceNumber(context, v.alGet('tx'));
        ty = alCoerceNumber(context, v.alGet('ty'));
    }
    return new Matrix(a, b, c, d, tx, ty);
}
export function copyAS3MatrixTo(m, v) {
    v.alPut('a', m.a);
    v.alPut('b', m.b);
    v.alPut('c', m.c);
    v.alPut('d', m.d);
    v.alPut('tx', m.tx);
    v.alPut('ty', m.ty);
}
var AVM1Matrix = /** @class */ (function (_super) {
    __extends(AVM1Matrix, _super);
    function AVM1Matrix(context, a, b, c, d, tx, ty) {
        if (a === void 0) { a = 1; }
        if (b === void 0) { b = 0; }
        if (c === void 0) { c = 0; }
        if (d === void 0) { d = 1; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.globals.Matrix.alGetPrototypeProperty();
        _this.alPut('a', a);
        _this.alPut('b', b);
        _this.alPut('c', c);
        _this.alPut('d', d);
        _this.alPut('tx', tx);
        _this.alPut('ty', ty);
        return _this;
    }
    AVM1Matrix.fromAS3Matrix = function (context, m) {
        return new AVM1Matrix(context, m.a, m.b, m.c, m.d, m.tx, m.ty);
    };
    return AVM1Matrix;
}(AVM1Object));
export { AVM1Matrix };
var AVM1MatrixFunction = /** @class */ (function (_super) {
    __extends(AVM1MatrixFunction, _super);
    function AVM1MatrixFunction(context) {
        var _this = _super.call(this, context) || this;
        alDefineObjectProperties(_this, {
            prototype: {
                value: new AVM1MatrixPrototype(context, _this)
            }
        });
        return _this;
    }
    AVM1MatrixFunction.prototype.alConstruct = function (args) {
        if (args && args.length > 0) {
            return new AVM1Matrix(this.context, args[0], args[1], args[2], args[3], args[4], args[5]);
        }
        else {
            return new AVM1Matrix(this.context);
        }
    };
    return AVM1MatrixFunction;
}(AVM1Function));
export { AVM1MatrixFunction };
var AVM1MatrixPrototype = /** @class */ (function (_super) {
    __extends(AVM1MatrixPrototype, _super);
    function AVM1MatrixPrototype(context, fn) {
        var _this = _super.call(this, context) || this;
        _this.alPrototype = context.builtins.Object.alGetPrototypeProperty();
        alDefineObjectProperties(_this, {
            constructor: {
                value: fn,
                writable: true
            },
            clone: {
                value: _this.clone,
                writable: true
            },
            concat: {
                value: _this.concat,
                writable: true
            },
            createBox: {
                value: _this.createBox,
                writable: true
            },
            createGradientBox: {
                value: _this.createGradientBox,
                writable: true
            },
            deltaTransformPoint: {
                value: _this.deltaTransformPoint,
                writable: true
            },
            identity: {
                value: _this.identity,
                writable: true
            },
            invert: {
                value: _this.invert,
                writable: true
            },
            rotate: {
                value: _this.rotate,
                writable: true
            },
            scale: {
                value: _this.scale,
                writable: true
            },
            toString: {
                value: _this._toString,
                writable: true
            },
            transformPoint: {
                value: _this.transformPoint,
                writable: true
            },
            translate: {
                value: _this.translate,
                writable: true
            }
        });
        return _this;
    }
    AVM1MatrixPrototype.prototype.clone = function () {
        var result = new AVM1Matrix(this.context);
        if (this instanceof AVM1Object) {
            result.alPut('a', this.alGet('a'));
            result.alPut('b', this.alGet('b'));
            result.alPut('c', this.alGet('c'));
            result.alPut('d', this.alGet('d'));
            result.alPut('tx', this.alGet('tx'));
            result.alPut('ty', this.alGet('ty'));
        }
        return result;
    };
    AVM1MatrixPrototype.prototype.concat = function (other) {
        var m = toAS3Matrix(this), m2 = toAS3Matrix(other);
        m.concat(m2);
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype.createBox = function (scaleX, scaleY, rotation, tx, ty) {
        if (rotation === void 0) { rotation = 0; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        scaleX = alCoerceNumber(this.context, scaleX);
        scaleY = alCoerceNumber(this.context, scaleY);
        rotation = alCoerceNumber(this.context, rotation);
        tx = alCoerceNumber(this.context, tx);
        ty = alCoerceNumber(this.context, ty);
        var m = toAS3Matrix(this);
        m.createBox(scaleX, scaleY, rotation, tx, ty);
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype.createGradientBox = function (width, height, rotation, tx, ty) {
        if (rotation === void 0) { rotation = 0; }
        if (tx === void 0) { tx = 0; }
        if (ty === void 0) { ty = 0; }
        width = alCoerceNumber(this.context, width);
        height = alCoerceNumber(this.context, height);
        rotation = alCoerceNumber(this.context, rotation);
        tx = alCoerceNumber(this.context, tx);
        ty = alCoerceNumber(this.context, ty);
        var m = toAS3Matrix(this);
        m.createGradientBox(width, height, rotation, tx, ty);
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype.deltaTransformPoint = function (pt) {
        var p = toAS3Point(pt);
        var m = toAS3Matrix(this);
        return AVM1Point.fromAS3Point(this.context, m.deltaTransformPoint(p));
    };
    AVM1MatrixPrototype.prototype.identity = function () {
        this.alPut('a', 1);
        this.alPut('b', 0);
        this.alPut('c', 0);
        this.alPut('d', 1);
        this.alPut('tx', 0);
        this.alPut('ty', 0);
    };
    AVM1MatrixPrototype.prototype.invert = function () {
        var m = toAS3Matrix(this);
        m.invert();
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype.rotate = function (angle) {
        angle = alCoerceNumber(this.context, angle);
        var m = toAS3Matrix(this);
        m.rotate(angle);
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype.scale = function (sx, sy) {
        sx = alCoerceNumber(this.context, sx);
        sy = alCoerceNumber(this.context, sy);
        var m = toAS3Matrix(this);
        m.scale(sx, sy);
        copyAS3MatrixTo(m, this);
    };
    AVM1MatrixPrototype.prototype._toString = function () {
        return toAS3Matrix(this).toString();
    };
    AVM1MatrixPrototype.prototype.transformPoint = function (pt) {
        var p = toAS3Point(pt);
        var m = toAS3Matrix(this);
        return AVM1Point.fromAS3Point(this.context, m.transformPoint(p));
    };
    AVM1MatrixPrototype.prototype.translate = function (tx, ty) {
        tx = alCoerceNumber(this.context, tx);
        ty = alCoerceNumber(this.context, ty);
        var m = toAS3Matrix(this);
        m.translate(tx, ty);
        copyAS3MatrixTo(m, this);
    };
    return AVM1MatrixPrototype;
}(AVM1Object));
export { AVM1MatrixPrototype };
